import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import ForbiddenPage from '../../pages/ForbiddenPage';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSiteAdmin?: boolean;
}

export function ProtectedRoute({ children, requireSiteAdmin }: ProtectedRouteProps) {
  const { isAuthenticated, isSiteAdmin, isLoading } = useAuth();
  const { hasPathAccess } = usePermissions();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireSiteAdmin && !isSiteAdmin) {
    return <Navigate to="/" replace />;
  }

  // Site admins can only access admin routes + profile (skip if already on /admin to avoid loop)
  if (isSiteAdmin && !requireSiteAdmin && !location.pathname.startsWith('/admin') && location.pathname !== '/profile') {
    return <Navigate to="/admin" replace />;
  }

  // Check per-module permissions (skip for non-module paths like /membres, /invite, etc.)
  if (!isSiteAdmin && !hasPathAccess(location.pathname)) {
    return <ForbiddenPage />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
